import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { Volleyball, CreditCard, CheckCircle, ArrowLeft, Phone, UploadSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getNextSaturdays(count = 6, pastCount = 2) {
  const out = [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const cursor = new Date(today);
  const diff = (6 - cursor.getDay() + 7) % 7;
  cursor.setDate(cursor.getDate() + diff);
  cursor.setDate(cursor.getDate() - pastCount * 7);
  for (let i = 0; i < count + pastCount; i++) {
    const yyyy = cursor.getFullYear();
    const mm = String(cursor.getMonth() + 1).padStart(2, '0');
    const dd = String(cursor.getDate()).padStart(2, '0');
    out.push(`${yyyy}-${mm}-${dd}`);
    cursor.setDate(cursor.getDate() + 7);
  }
  return out;
}

function formatDateLocale(ymd, lang) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return lang === 'en' ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
}

export default function JogoTreinoPublic() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', preferred_date: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [regId, setRegId] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const saturdays = getNextSaturdays(6);
  const dateErr = formData.preferred_date && (() => {
    const [y, m, d] = formData.preferred_date.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (dt.getDay() !== 6) return t('jogoTreino.saturdayOnly');
    return null;
  })();

  const isValid = formData.name && formData.phone && formData.preferred_date && !dateErr;

  const handlePayWithCard = async () => {
    setLoading(true);
    try {
      const { data: reg } = await axios.post(`${API}/jogo-treino/register`, formData);
      const { data: checkout } = await axios.post(`${API}/jogo-treino/checkout`, { registration_id: reg.id, origin_url: window.location.origin });
      if (checkout.url) window.location.assign(checkout.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorProcessing'));
      setLoading(false);
    }
  };

  const handlePayWithZelle = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/jogo-treino/register`, {
        ...formData,
        notes: (formData.notes ? formData.notes + ' | ' : '') + 'Pagamento via Zelle'
      });
      setRegId(data.id);
      setStep('zelle-info');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorProcessing'));
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file || !regId) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/jogo-treino/zelle-receipt?registration_id=${regId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('common.receiptSent'));
      setStep('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorProcessing'));
    } finally {
      setUploadingReceipt(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="jogo-treino-public">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-tk.png" alt="Logo" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium">{t('common.doLogin')}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {step === 'success' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center" data-testid="jt-success">
            <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-white" weight="fill" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">{t('jogoTreino.confirmedTitle')}</h2>
            <p className="text-[#73706A] mb-6">{t('jogoTreino.confirmedSubtitle')}</p>
            <Button
              onClick={() => { setStep('form'); setFormData({ name: '', phone: '', email: '', preferred_date: '', notes: '' }); setRegId(null); }}
              className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
              data-testid="jt-new-btn"
            >
              {t('common.newRegistration')}
            </Button>
          </div>
        )}

        {step === 'zelle-info' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center space-y-6" data-testid="jt-zelle">
            <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto">
              <Phone size={40} className="text-white" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{t('dropIn.zelleTitle')}</h2>
              <p className="text-[#73706A] mt-2">{t('courtRental.zelleHeader')} <strong className="text-[#2D6A4F]">$25.00</strong> {t('courtRental.zelleHeaderSuffix')}</p>
            </div>
            <div className="bg-[#F5F2EB] rounded-lg p-6">
              <p className="text-3xl font-bold text-[#1A1A1A] font-['Outfit'] tracking-wider">5618596830</p>
              <p className="text-sm text-[#73706A] mt-2">Tia Karol Sports</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <p className="text-sm text-yellow-800"><strong>{t('common.important')}:</strong> {t('common.zelleNoteShort')}</p>
            </div>
            <label className="cursor-pointer block">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => handleReceiptUpload(e.target.files[0])} data-testid="jt-zelle-upload" />
              <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploadingReceipt}>
                {uploadingReceipt ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={20} className="mr-2" />}
                {uploadingReceipt ? t('common.sending') : t('common.uploadReceipt')}
              </Button>
            </label>
          </div>
        )}

        {step === 'form' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center">
              <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Volleyball size={40} className="text-white" weight="fill" />
              </div>
              <h1 className="font-['Outfit'] text-3xl font-bold text-[#1A1A1A] mb-2">{t('jogoTreino.title')}</h1>
              <p className="text-[#73706A]">{t('jogoTreino.subtitle')}</p>
              <p className="text-3xl font-bold text-[#2D6A4F] font-['Outfit'] mt-3">$25.00</p>
              <p className="text-xs text-[#73706A]">{t('common.card')}: $26.25 (+5% {t('common.tax').toLowerCase()}) | Zelle: $25.00</p>
            </div>

            <div className="bg-white border border-[#E6E2D8] rounded-xl p-6">
              <h2 className="font-['Outfit'] font-semibold text-xl text-[#1A1A1A] mb-4">{t('jogoTreino.regDataTitle')}</h2>
              <div className="space-y-4">
                <div>
                  <Label>{t('common.fullName')} *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('common.yourName')} required data-testid="jt-name" />
                </div>
                <div>
                  <Label>{t('common.phone')} *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(000) 000-0000" required data-testid="jt-phone" />
                </div>
                <div>
                  <Label>{t('jogoTreino.emailOptional')}</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('login.emailPlaceholder')} data-testid="jt-email" />
                </div>
                <div>
                  <Label>{t('jogoTreino.preferredDateSaturday')} *</Label>
                  <Input
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                    required
                    data-testid="jt-date"
                    className={dateErr ? 'border-[#9B2226]' : ''}
                  />
                  {dateErr && <p className="text-xs text-[#9B2226] mt-1" data-testid="jt-date-error">{dateErr}</p>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {saturdays.map(d => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setFormData({ ...formData, preferred_date: d })}
                        className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                          formData.preferred_date === d
                            ? 'bg-[#2D6A4F] border-[#2D6A4F] text-white'
                            : 'bg-white border-[#E6E2D8] text-[#1A1A1A] hover:border-[#2D6A4F]'
                        }`}
                        data-testid={`jt-quick-${d}`}
                      >
                        {formatDateLocale(d, i18n.language)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>{t('common.notes')}</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input-field min-h-[60px] resize-none"
                    placeholder={t('dropInFem.notesPh')}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button onClick={handlePayWithCard} disabled={!isValid || loading} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white py-4" data-testid="jt-card-btn">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={20} className="mr-2" /> {t('common.card')} $26.25</>}
              </Button>
              <Button onClick={handlePayWithZelle} disabled={!isValid || loading} variant="outline" className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-4" data-testid="jt-zelle-btn">
                <Phone size={20} className="mr-2" /> Zelle $25.00
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#73706A] hover:text-[#1A1A1A] inline-flex items-center gap-2">
            <ArrowLeft size={16} /> {t('common.back')}
          </Link>
        </div>
      </main>
    </div>
  );
}
